import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package as PackageIcon, Upload, X, FileText } from 'lucide-react';
import { CourierPackageApi } from '@shared/sdk-v1/apis';
import { NewCourierPackage } from '@shared/sdk-v1/models';
import { apiConfig } from '../shared/api-config';
import { useAuth } from '../auth/hooks/useAuth';
import { showErrorToast, showSuccessToast } from '../utils/toast';
import { useFileUpload } from '../hooks/useFileUpload';
import { InputField } from '@shared/components';
import { useAppDarkMode } from '../hooks/useAppDarkMode';

const AddPackage = () => {
  const navigate = useNavigate();
  const { darkMode } = useAppDarkMode();
  const { user, courier } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string>('');
  const { uploadFiles, isUploading, error: uploadError } = useFileUpload();

  // Form state
  const [trackingNumber, setTrackingNumber] = useState('');
  const [description, setDescription] = useState('');
  const [value, setValue] = useState('');
  const [merchant, setMerchant] = useState('');
  const [itemUrl, setItemUrl] = useState('');
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);

  // Error state
  const [errors, setErrors] = useState({
    trackingNumber: '',
    description: '',
    value: '',
    merchant: ''
  });

  // Merchant options
  const merchantOptions = [{
    value: 'amazon',
    label: 'Amazon'
  }, {
    value: 'ebay',
    label: 'eBay'
  }, {
    value: 'shein',
    label: 'Shein'
  }, {
    value: 'temu',
    label: 'Temu'
  }, {
    value: 'walmart',
    label: 'Walmart'
  }, {
    value: 'aliexpress',
    label: 'AliExpress'
  }, {
    value: 'etsy',
    label: 'Etsy'
  }, {
    value: 'other',
    label: 'Other'
  }];

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setInvoiceFile(e.target.files[0]);
    }
  };

  // Remove selected file
  const removeFile = () => {
    setInvoiceFile(null);
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {
      trackingNumber: '',
      description: '',
      value: '',
      merchant: ''
    };
    let isValid = true;

    if (!trackingNumber.trim()) {
      newErrors.trackingNumber = 'Tracking number is required';
      isValid = false;
    }

    if (!description.trim()) {
      newErrors.description = 'Description is required';
      isValid = false;
    }

    if (!value.trim()) {
      newErrors.value = 'Value is required';
      isValid = false;
    } else if (isNaN(parseFloat(value))) {
      newErrors.value = 'Value must be a number';
      isValid = false;
    }

    if (!merchant) {
      newErrors.merchant = 'Merchant is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    // Check if user and courier are available
    if (!user?.id || !courier?.id) {
      showErrorToast({
        title: 'Error',
        message: 'User or courier information not available. Please log in again.',
      });
      return;
    }

    setIsSubmitting(true);
    setApiError('');

    try {
      const packageApi = new CourierPackageApi(apiConfig());

      // Upload the invoice BEFORE creating the package so we can persist the
      // name the server assigned it. loopback-component-storage runs with
      // nameConflict:"makeUnique" and renames every upload to `<uuid><ext>`
      // before storage-mirror copies it to R2 under `invoices-{courierId}/{name}`.
      // Storing the browser's File.name instead would point at an object that
      // does not exist, 404ing every later view/download.
      // Uploading first also means a failed upload can't leave a package
      // referencing a file that was never stored.
      let attachedFileNames: string[] | undefined;
      let uploadFailed = false;

      if (invoiceFile) {
        try {
          const uploaded = await uploadFiles(
            [invoiceFile],
            'invoices',
            courier.id.toString(),
            '', // packageId — accepted by the hook but unused for this container
            []
          );
          const names = uploaded.map((file) => file.name).filter(Boolean);
          attachedFileNames = names.length ? names : undefined;
        } catch (uploadError) {
          console.error('Error uploading file:', uploadError);
          uploadFailed = true;
        }
      }

      // Prepare package data
      const packageData: NewCourierPackage = {
        trackingNo: trackingNumber.trim(),
        description: description.trim(),
        value: parseFloat(value),
        merchant: merchant,
        url: itemUrl.trim() || undefined,
        status: 'Pre Alert', // Default status for new packages
        ownerId: user.id,
        courierId: courier.id,
        preAlertDate: new Date(), // Set pre-alert date to current date
        attachedFile: attachedFileNames,
      };

      // Create the package
      const createdPackage = await packageApi.courierPackageCreate({
        data: packageData
      });

      if (createdPackage.id) {
        if (uploadFailed) {
          // The package is saved without the invoice — the customer can attach
          // it from the package details page, which has a working upload path.
          showErrorToast({
            title: 'Warning',
            message: 'Package created but file upload failed. You can try uploading the file again from the package details page.',
          });
          navigate('/packages');
          return;
        }

        showSuccessToast({
          title: 'Success',
          message: 'Package added successfully!',
        });
        navigate('/packages');
      } else {
        throw new Error('Failed to create package');
      }

    } catch (error: any) {
      console.error('Error creating package:', error);
      const errorMessage = error.response?.data?.error?.message ||
                          error.message ||
                          'Failed to create package. Please try again.';

      setApiError(errorMessage);
      showErrorToast({
        title: 'Error',
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate('/packages');
  };
  return <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-4xl mx-auto p-6 pb-24 md:pb-6">
        <div className="flex items-center mb-6">
          <div className="bg-orange-100 dark:bg-orange-900 rounded-full p-2 mr-3">
            <PackageIcon className="h-6 w-6 text-orange-500 dark:text-orange-400" />
          </div>
          <h1 className="text-2xl font-bold dark:text-white">Add Package</h1>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              {/* API Error Display */}
              {apiError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-lg p-4">
                  <p className="text-red-700 dark:text-red-300 text-sm">
                    {apiError}
                  </p>
                </div>
              )}

              {/* Tracking Number */}
              <div>
                <InputField
                  darkMode={darkMode}
                  type="text"
                  name="trackingNumber"
                  label="Tracking Number"
                  value={trackingNumber}
                  onChange={e => setTrackingNumber(e.target.value)}
                  placeholder="Enter tracking number"
                  required
                  fullWidth
                />
                {errors.trackingNumber && (
                  <p className="mt-1 text-sm text-red-500">{errors.trackingNumber}</p>
                )}
              </div>
              {/* Description */}
              <div>
                <InputField
                  darkMode={darkMode}
                  type="text"
                  name="description"
                  label="Description"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Enter package description"
                  required
                  fullWidth
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-500">{errors.description}</p>
                )}
              </div>
              {/* Value in USD */}
              <div>
                <InputField
                  darkMode={darkMode}
                  type="number"
                  name="value"
                  label="Value in USD"
                  value={value}
                  onChange={e => setValue(e.target.value)}
                  placeholder="0.00"
                  required
                  fullWidth
                  min="0"
                  step="0.01"
                />
                {errors.value && (
                  <p className="mt-1 text-sm text-red-500">{errors.value}</p>
                )}
              </div>
              {/* Merchant */}
              <div>
                <InputField
                  darkMode={darkMode}
                  type="select"
                  name="merchant"
                  label="Merchant"
                  value={merchant}
                  onChange={e => setMerchant(e.target.value)}
                  options={merchantOptions}
                  required
                  fullWidth
                />
                {errors.merchant && (
                  <p className="mt-1 text-sm text-red-500">{errors.merchant}</p>
                )}
              </div>
              {/* Item URL (Optional) */}
              <div>
                <InputField
                  darkMode={darkMode}
                  type="text"
                  name="itemUrl"
                  label="Item URL (Optional)"
                  value={itemUrl}
                  onChange={e => setItemUrl(e.target.value)}
                  placeholder="https://"
                  fullWidth
                />
              </div>
              {/* Attach Invoice */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Attach Invoice
                </label>
                {!invoiceFile ? <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                    <label className="cursor-pointer block w-full">
                      <div className="flex flex-col items-center">
                        <Upload className="h-8 w-8 text-gray-400 dark:text-gray-500 mb-2" />
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          Click to upload or drag and drop
                        </span>
                        <span className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          PDF, JPG, PNG (max 5MB)
                        </span>
                      </div>
                      <input type="file" className="hidden" onChange={handleFileSelect} accept=".pdf,.jpg,.jpeg,.png" />
                    </label>
                  </div> : <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 flex items-center justify-between">
                    <div className="flex items-center overflow-hidden">
                      <FileText className="h-5 w-5 mr-2 text-orange-500 flex-shrink-0" />
                      <div className="overflow-hidden">
                        <span className="text-sm font-medium block truncate dark:text-white">
                          {invoiceFile.name}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {(invoiceFile.size / 1024).toFixed(0)} KB
                        </span>
                      </div>
                    </div>
                    <button type="button" onClick={removeFile} className="text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400">
                      <X className="h-5 w-5" />
                    </button>
                  </div>}
              </div>
              {/* Form Actions */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button type="submit" disabled={isSubmitting} className="flex-1 bg-blue-500 hover:bg-blue-600 text-white font-medium py-3 px-4 rounded-md shadow-sm disabled:bg-blue-300 dark:disabled:bg-blue-800 flex items-center justify-center">
                  {isSubmitting ? <>
                      <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                      Saving...
                    </> : 'Save Package'}
                </button>
                <button type="button" onClick={handleCancel} className="flex-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium py-3 px-4 rounded-md shadow-sm">
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>;
};
export default AddPackage;
